import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import ThemeToggle from '../components/ThemeToggle';
import { Users, Trash2, UserPlus, Shield, User } from 'lucide-react';
import './Clients.css';

function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminData, setAdminData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberFirstName, setNewMemberFirstName] = useState('');
  const [newMemberLastName, setNewMemberLastName] = useState('');
  const [newMemberPassword, setNewMemberPassword] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');
  const [addingMember, setAddingMember] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) {
      navigate('/admin/login');
      return;
    }
    
    const data = JSON.parse(stored);
    setAdminData(data);
    setIsSuperAdmin(data.role === 'super_admin');
    
    // Only super admins can access this page
    if (data.role !== 'super_admin') {
      navigate('/');
      return;
    }
    
    fetchClients();
  }, [navigate]);

  const fetchClients = async () => {
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch('https://live-event-photos.onrender.com/api/admin/clients', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clients');
      }

      const data = await response.json();
      setClients(data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (clientId, clientEmail) => {
    if (!window.confirm(`Are you sure you want to delete ${clientEmail}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`https://live-event-photos.onrender.com/api/admin/clients/${clientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete client');
      }

      // Refresh clients list
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Failed to delete client. Please try again.');
    }
  };

  const handleManageTeam = async (client) => {
    setSelectedClient(client);
    setShowTeamModal(true);
    setLoadingTeam(true);
    
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`https://api.eventpiclive.com/api/admin/clients/${client.id}/team`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch team members');
      }
      
      const data = await response.json();
      setTeamMembers(data.admins || []);
    } catch (error) {
      console.error('Error fetching team:', error);
      alert('Failed to load team members');
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleAddTeamMember = async () => {
    if (!newMemberEmail || !newMemberFirstName || !newMemberLastName || !newMemberPassword) {
      alert('Please fill in all fields');
      return;
    }
    
    setAddingMember(true);
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`https://api.eventpiclive.com/api/admin/clients/${selectedClient.id}/team`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: newMemberEmail,
          firstName: newMemberFirstName,
          lastName: newMemberLastName,
          password: newMemberPassword,
          role: newMemberRole
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add team member');
      }
      
      alert('Team member added successfully!');
      setShowAddTeamMemberModal(false);
      setNewMemberEmail('');
      setNewMemberFirstName('');
      setNewMemberLastName('');
      setNewMemberPassword('');
      setNewMemberRole('member');
      
      // Reload team members
      handleManageTeam(selectedClient);
    } catch (error) {
      console.error('Error adding team member:', error);
      alert(error.message);
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveTeamMember = async (adminId, adminName) => {
    if (!window.confirm(`Remove ${adminName} from this client's team?`)) {
      return;
    }
    
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`https://api.eventpiclive.com/api/admin/clients/${selectedClient.id}/team/${adminId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove team member');
      }
      
      alert('Team member removed successfully!');
      
      // Reload team members
      handleManageTeam(selectedClient);
    } catch (error) {
      console.error('Error removing team member:', error);
      alert(error.message);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/admin/login');
  };

  const filteredClients = clients.filter(client =>
    client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <ThemeToggle />
        <Sidebar onLogout={handleLogout} isSuperAdmin={isSuperAdmin} />
        <div className="events-content">
          <div className="clients-loading">Loading clients...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <ThemeToggle />
      <Sidebar onLogout={handleLogout} isSuperAdmin={isSuperAdmin} />
      
      <div className="events-content">
        <div className="events-top">
          <h1 style={{ fontSize: '32px', fontWeight: '600', margin: 0 }}>Client Management</h1>
          <button 
            onClick={() => navigate('/admin/create-client')} 
            className="btn-primary"
          >
            + Create Client
          </button>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{ maxWidth: '400px' }}
          />
        </div>

        {filteredClients.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
            {searchTerm ? 'No clients match your search.' : 'No clients yet.'}
          </div>
        ) : (
          <div className="clients-grid">
            {filteredClients.map(client => (
              <div key={client.id} className="client-card">
                <div className="client-header">
                  <div className="client-avatar">
                    {client.full_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="client-info">
                    <h3>{client.full_name}</h3>
                    <p className="client-email">{client.email}</p>
                  </div>
                </div>

                <div className="client-details">
                  <div className="detail-row">
                    <span className="detail-label">Subscription:</span>
                    <span className="detail-value">{client.subscription_tier}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span className={`status-badge ${client.subscription_status}`}>
                      {client.subscription_status}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{client.phone || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Joined:</span>
                    <span className="detail-value">
                      {client.created_at ? new Date(client.created_at).toLocaleDateString() : 'Invalid Date'}
                    </span>
                  </div>
                </div>

                <div className="client-actions">
                  <button 
                    className="btn-secondary"
                    onClick={() => navigate(`/admin/clients/${client.id}/manage`)}
                    style={{display: 'flex', alignItems: 'center', gap: '8px'}}
                  >
                    <Users size={16} />
                    Manage Client
                  </button>
                  <button 
                    className="btn-danger"
                    onClick={() => handleDeleteClient(client.id, client.email)}
                    style={{flex: 1}}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Clients;
